// src/components/ExamMaterialPanel.jsx
// Body content for an exhibit/requirement/help-document ExamFloatingPanel -
// either plain authored text, or an embedded multi-page PDF viewer, matching
// the reference exam software's own document panel: all pages in one
// continuous scroll (not one page at a time), the page width auto-fitting
// the panel's own width (resizing the panel grows the text without having
// to reach for zoom - zoom is still there as a manual override on top of
// that), and - for exhibits/requirements specifically, not the shared Help/
// Tax Tables document - the ability to highlight or strike through text.
//
// Highlight/Strikethrough are triggered from PERSISTENT buttons in
// ExamSplitScreen's header (next to Scratch Pad/Calculator), not from a
// popup here - matching the real exam software's toolbar, where those
// buttons live alongside Scratch Pad rather than only appearing after a
// selection is made. This component just captures the current text
// selection (if any) on mouseup and exposes applyHighlight/
// applyStrikethrough via a ref for ExamSplitScreen to call on whichever
// document panel is frontmost when a button is clicked.
//
// Highlights/strikethrough are stored as percentage-of-page rectangles
// (captured from the browser's own text selection at the moment they're
// made), not as DOM node references - react-pdf tears down and rebuilds the
// text layer on every zoom/resize, which would otherwise wipe out anything
// anchored to a specific DOM node. Percentages naturally re-scale to
// whatever size the page renders at next, so a single stored mark keeps
// lining up correctly forever. They're personal study aids only - saved to
// the student's own exam session, never read by generateExamPdf.js or the
// abandoned-session recovery renderer, so they can never end up in front of
// a marker.

import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDF_WORKER_SRC } from '../utils/pdfWorker';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { db } from '../firebase/config';
import { useWriteGuard } from '../utils/writeGuard';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC; // legacy build on older browsers - see utils/pdfWorker.js

const toolbarBtnStyle = {
  background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px',
  width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', color: '#4a5568', flexShrink: 0
};

const CONTAINER_PADDING = 24; // matches the scroll container's own padding below
const AUTOSAVE_DEBOUNCE_MS = 5000; // marks are discrete clicks, not keystrokes - no need to wait as long as a text autosave

const ExamMaterialPanel = forwardRef(({ text, pdfUrl, sessionId, materialId, initialMarks }, ref) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [fitWidth, setFitWidth] = useState(400);
  const [marks, setMarks] = useState(() => initialMarks || []);
  const scrollRef = useRef(null);
  const pageRefs = useRef({}); // pageNumber -> element, for scrollIntoView and selection-rect math
  const marksRef = useRef(marks);
  const pendingSelectionRef = useRef(null); // last captured selection - not React state, since it doesn't need to re-render anything itself
  const debounceTimerRef = useRef(null);
  const { guard } = useWriteGuard();

  useEffect(() => { marksRef.current = marks; }, [marks]);

  const saveMarks = useCallback(() => {
    if (debounceTimerRef.current) { clearTimeout(debounceTimerRef.current); debounceTimerRef.current = null; }
    if (!sessionId || !materialId) return;
    const value = marksRef.current;
    guard(async () => {
      try {
        await updateDoc(doc(db, 'examSessions', sessionId), {
          [`exhibitMarks.${materialId}`]: value,
          [`exhibitMarksUpdatedAt.${materialId}`]: serverTimestamp(),
          // Refreshes the session-level idle clock sweepAbandonedExamSessions
          // uses for unlimited-time sessions (functions/index.js) - see
          // ExamAnswerPanel's identical field for the full reasoning.
          lastAutosaveAt: serverTimestamp()
        });
      } catch (err) {
        console.error('Exhibit marks autosave failed:', err);
      }
    });
  }, [sessionId, materialId, guard]);

  const scheduleSave = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(saveMarks, AUTOSAVE_DEBOUNCE_MS);
  }, [saveMarks]);

  useEffect(() => () => { if (debounceTimerRef.current) saveMarks(); }, [saveMarks]);

  // Auto-fit: the PDF's rendered width tracks the panel's own width, so
  // dragging the floating panel bigger grows the text automatically -
  // matching the real exam software instead of requiring a manual zoom.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width;
      if (width) setFitWidth(Math.max(200, width - CONTAINER_PADDING));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Tracks which page is most in view while the student scrolls, so the
  // "page X / N" indicator (and the Prev/Next buttons) reflect real scroll
  // position instead of only updating on a button click.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || !numPages) return;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter(e => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) {
        const page = Number(visible.target.dataset.page);
        if (page) setCurrentPage(page);
      }
    }, { root, threshold: [0.25, 0.5, 0.75] });
    Object.values(pageRefs.current).forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numPages]);

  const goToPage = useCallback((page) => {
    pageRefs.current[page]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Captures the current browser text selection (if any) as percentage-of-
  // page rectangles, ready for ExamSplitScreen's Highlight/Strikethrough
  // buttons to act on. Only relevant for PDF exhibits/requirements -
  // materialId is only passed for those, not the shared Help document.
  const handleMouseUp = useCallback(() => {
    if (!materialId) return;
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !selection.toString().trim()) {
      return;
    }
    const range = selection.getRangeAt(0);
    const anchorEl = range.commonAncestorContainer.nodeType === 1
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentElement;
    const pageEl = anchorEl?.closest('[data-page]');
    if (!pageEl) return;
    const pageNumber = Number(pageEl.dataset.page);
    const pageRect = pageEl.getBoundingClientRect();
    const clientRects = Array.from(range.getClientRects());
    if (!clientRects.length || !pageRect.width || !pageRect.height) return;

    const rects = clientRects.map(r => ({
      xPct: ((r.left - pageRect.left) / pageRect.width) * 100,
      yPct: ((r.top - pageRect.top) / pageRect.height) * 100,
      wPct: (r.width / pageRect.width) * 100,
      hPct: (r.height / pageRect.height) * 100
    }));

    pendingSelectionRef.current = { pageNumber, rects };
  }, [materialId]);

  const applyMark = (type, color) => {
    const pending = pendingSelectionRef.current;
    if (!pending) return false;
    const mark = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      pageNumber: pending.pageNumber,
      type,
      color: color || null,
      rects: pending.rects
    };
    setMarks(prev => [...prev, mark]);
    scheduleSave();
    window.getSelection()?.removeAllRanges();
    pendingSelectionRef.current = null;
    return true;
  };

  const rectsOverlap = (a, b) =>
    !(a.xPct + a.wPct <= b.xPct || b.xPct + b.wPct <= a.xPct || a.yPct + a.hPct <= b.yPct || b.yPct + b.hPct <= a.yPct);

  // Removes any existing mark of the given type that overlaps the current
  // selection, rather than requiring the student to click each colored
  // rectangle individually - the explicit "Remove Highlight" option below,
  // and strikethrough's own toggle-off, both go through this. Filters the
  // current `marks` state directly (not a functional setMarks updater) so
  // `removedAny` is known synchronously - a setMarks updater's body doesn't
  // actually run until React processes the update, too late to use here.
  const removeOverlappingMarks = (type) => {
    const pending = pendingSelectionRef.current;
    if (!pending) return false;
    let removedAny = false;
    const next = marks.filter(m => {
      if (m.type !== type || m.pageNumber !== pending.pageNumber) return true;
      const overlaps = m.rects.some(mr => pending.rects.some(pr => rectsOverlap(mr, pr)));
      if (overlaps) removedAny = true;
      return !overlaps;
    });
    if (removedAny) {
      setMarks(next);
      scheduleSave();
      window.getSelection()?.removeAllRanges();
      pendingSelectionRef.current = null;
    }
    return removedAny;
  };

  useImperativeHandle(ref, () => ({
    applyHighlight: (color) => applyMark('highlight', color),
    removeHighlight: () => removeOverlappingMarks('highlight'),
    // A single button, not a color picker, so it toggles: selecting already
    // struck-through text removes the strike instead of adding a second one.
    applyStrikethrough: () => (removeOverlappingMarks('strike') || applyMark('strike')),
    // Lets ExamSplitScreen tell whether this is even a document that
    // supports marking, before bothering to check for a pending selection.
    supportsMarking: !!materialId
  }));

  const removeMark = (id) => {
    setMarks(prev => prev.filter(m => m.id !== id));
    scheduleSave();
  };

  if (text != null) {
    return (
      <div style={{ padding: '16px', fontSize: '14px', lineHeight: 1.7, color: '#2c3e50', whiteSpace: 'pre-wrap' }}>
        {text}
      </div>
    );
  }

  if (!pdfUrl) {
    return <div style={{ padding: '16px', color: '#a0aec0', fontSize: '13px' }}>Loading...</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px',
        borderBottom: '1px solid #e2e8f0', background: '#f8f9fa', flexShrink: 0, fontSize: '12px'
      }}>
        <button type="button" onClick={() => goToPage(Math.max(1, currentPage - 1))} disabled={currentPage <= 1} style={toolbarBtnStyle}>
          <ChevronLeft size={14} />
        </button>
        <span style={{ color: '#4a5568', minWidth: '54px', textAlign: 'center' }}>
          {currentPage} / {numPages || '-'}
        </span>
        <button type="button" onClick={() => goToPage(Math.min(numPages || 1, currentPage + 1))} disabled={!numPages || currentPage >= numPages} style={toolbarBtnStyle}>
          <ChevronRight size={14} />
        </button>
        <div style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 4px' }} />
        <button type="button" onClick={() => setZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)))} style={toolbarBtnStyle}>
          <ZoomOut size={14} />
        </button>
        <span style={{ color: '#4a5568', minWidth: '38px', textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
        <button type="button" onClick={() => setZoom(z => Math.min(2.5, +(z + 0.25).toFixed(2)))} style={toolbarBtnStyle}>
          <ZoomIn size={14} />
        </button>
        {materialId && (
          <>
            <div style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 4px' }} />
            <span style={{ color: '#a0aec0', fontSize: '11px' }}>Select text, then use Highlight/Strikethrough above</span>
          </>
        )}
      </div>
      <div
        ref={scrollRef}
        onMouseUp={handleMouseUp}
        style={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'auto', background: '#e2e8f0', padding: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}
      >
        <Document
          file={pdfUrl}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          loading={<div style={{ padding: '20px', color: '#718096' }}>Loading...</div>}
        >
          {/* Shows page 1 optimistically before the page count is known, then
              expands to the full document once onLoadSuccess fires. */}
          {Array.from({ length: numPages || 1 }, (_, i) => i + 1).map(page => (
            <div
              key={page}
              ref={(el) => { pageRefs.current[page] = el; }}
              data-page={page}
              style={{ position: 'relative', display: 'inline-block' }}
            >
              <Page pageNumber={page} width={fitWidth * zoom} />
              {marks.filter(m => m.pageNumber === page).map(mark => (
                mark.rects.map((r, ri) => (
                  <div
                    key={`${mark.id}-${ri}`}
                    onClick={() => removeMark(mark.id)}
                    title="Click to remove"
                    style={mark.type === 'strike' ? {
                      position: 'absolute', left: `${r.xPct}%`, top: `${r.yPct + r.hPct / 2}%`,
                      width: `${r.wPct}%`, height: '2px', background: '#c33329',
                      cursor: 'pointer', pointerEvents: 'auto'
                    } : {
                      position: 'absolute', left: `${r.xPct}%`, top: `${r.yPct}%`,
                      width: `${r.wPct}%`, height: `${r.hPct}%`, background: mark.color,
                      opacity: 0.45, mixBlendMode: 'multiply',
                      cursor: 'pointer', pointerEvents: 'auto'
                    }}
                  />
                ))
              ))}
            </div>
          ))}
        </Document>
      </div>
    </div>
  );
});

export default ExamMaterialPanel;
